import { FormEvent, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, apiErrorMessage, blobErrorMessage } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import type { DocumentTemplate } from '../api/types';
import PdfPreviewModal from '../components/PdfPreviewModal';
import PlaceholdersHelp from '../components/PlaceholdersHelp';

const DYNAMIC = new Set(['date', 'date_us', 'datetime', 'mes_ano', 'anomes', 'now', 'email_from', 'email_from_name', 'email_subject', 'email_body']);

export default function TemplatesPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  // Preview
  const [previewTpl, setPreviewTpl] = useState<DocumentTemplate | null>(null);
  const [ctxValues, setCtxValues] = useState<Record<string, string>>({});
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  function openPreview(t: DocumentTemplate) {
    setPreviewTpl(t);
    setCtxValues({});
    setPdfUrl(null);
    setPdfError(null);
  }

  function closePreview() {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPreviewTpl(null);
    setPdfUrl(null);
    setPdfError(null);
  }

  async function generatePreview() {
    if (!previewTpl) return;
    setPdfLoading(true);
    setPdfError(null);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    try {
      const res = await api.post(`/templates/${previewTpl.id}/preview`, { context: ctxValues }, { responseType: 'blob' });
      setPdfUrl(URL.createObjectURL(res.data));
    } catch (err) {
      setPdfError(await blobErrorMessage(err, 'Falha ao gerar preview'));
      setPdfUrl(null);
    } finally {
      setPdfLoading(false);
    }
  }

  const { data: templates } = useQuery({
    queryKey: ['templates'],
    queryFn: async () => (await api.get<DocumentTemplate[]>('/templates')).data,
  });

  const create = useMutation({
    mutationFn: async () => {
      const file = fileRef.current?.files?.[0];
      if (!file) throw new Error('Selecione um arquivo .docx ou .pdf');
      const fd = new FormData();
      fd.append('file', file);
      fd.append('name', name);
      const { data } = await api.post<DocumentTemplate>('/templates', fd);
      return data;
    },
    onSuccess: (tpl) => {
      qc.invalidateQueries({ queryKey: ['templates'] });
      setShowForm(false);
      setName('');
      if (fileRef.current) fileRef.current.value = '';
      toast.success(`${tpl.name} salvo · placeholders: ${tpl.placeholders.join(', ') || 'nenhum'}`);
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha no upload')),
  });

  const remove = useMutation({
    mutationFn: async (id: string) => (await api.delete(`/templates/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['templates'] }); toast.success('Template removido'); },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao remover')),
  });

  const download = useMutation({
    mutationFn: async (t: DocumentTemplate) => {
      const res = await api.get(`/templates/${t.id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = t.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    onError: (err: any) => toast.error(apiErrorMessage(err, 'Falha ao baixar')),
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    create.mutate();
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Templates de documento</h2>
        {!showForm && (
          <button className="btn btn-primary text-sm" onClick={() => setShowForm(true)}>
            + Novo template
          </button>
        )}
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card space-y-3">
          <h3 className="font-semibold">Subir template</h3>
          <div className="text-xs text-slate-600 bg-slate-100 rounded p-2 space-y-1">
            <div>
              <strong>DOCX:</strong> use placeholders <code>{'{{ nome }}'}</code> no texto do documento.
            </div>
            <div>
              <strong>PDF:</strong> precisa ter campos de formulário cujos nomes batem com os placeholders.
            </div>
            <div>
              <strong>XLSX:</strong> use placeholders <code>{'{{ nome }}'}</code> nas células da planilha.
            </div>
            <div>
              Há placeholders automáticos (data, mês/ano, dados do e-mail) — <PlaceholdersHelp />
            </div>
          </div>

          <div>
            <label className="block text-sm mb-1">Nome</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Recibo mensal"
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm mb-1">Arquivo (.docx, .pdf ou .xlsx)</label>
            <input ref={fileRef} type="file" accept=".docx,.pdf,.xlsx" className="text-sm" required />
          </div>

          <div className="flex gap-2">
            <button type="submit" className="btn btn-primary" disabled={create.isPending}>
              {create.isPending ? 'Enviando…' : 'Salvar'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowForm(false);
                setName('');
              }}
            >
              Cancelar
            </button>
          </div>
        </form>
      )}

      {templates?.length === 0 && !showForm && (
        <div className="card text-sm text-slate-600 text-center">Nenhum template ainda.</div>
      )}

      <div className="space-y-2">
        {templates?.map((t) => (
          <div key={t.id} className="card flex items-center justify-between">
            <div className="min-w-0">
              <div className="font-medium">
                {t.name} <span className="uppercase text-xs text-slate-400">{t.file_type}</span>
              </div>
              <div className="text-xs text-slate-500 truncate">{t.original_filename}</div>
              {t.placeholders.length > 0 && (
                <div className="text-xs text-slate-500 mt-1">
                  placeholders:{' '}
                  {t.placeholders.map((p) => (
                    <code key={p} className="bg-slate-100 rounded px-1 mr-1">
                      {p}
                    </code>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-2 shrink-0">
              <button className="btn btn-secondary text-xs py-1" onClick={() => openPreview(t)}>
                Pré-visualizar
              </button>
              <button className="btn btn-secondary text-xs py-1" onClick={() => download.mutate(t)} disabled={download.isPending}>
                Baixar
              </button>
              <button
                className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed px-2"
                disabled={remove.isPending}
                onClick={() => {
                  if (confirm(`Remover ${t.name}?`)) remove.mutate(t.id);
                }}
              >
                Remover
              </button>
            </div>
          </div>
        ))}
      </div>

      {previewTpl && (
        <PdfPreviewModal
          title={`Preview · ${previewTpl.name}`}
          url={pdfUrl}
          loading={pdfLoading}
          error={pdfError}
          onClose={closePreview}
        >
          {previewTpl.placeholders.filter((p) => !DYNAMIC.has(p)).map((p) => (
            <label key={p} className="block">
              <code className="text-xs">{p}</code>
              <input
                className="input"
                value={ctxValues[p] ?? ''}
                onChange={(e) => setCtxValues((v) => ({ ...v, [p]: e.target.value }))}
              />
            </label>
          ))}
          <button className="btn btn-primary text-sm" onClick={generatePreview} disabled={pdfLoading}>
            {pdfLoading ? 'Gerando…' : 'Gerar preview'}
          </button>
        </PdfPreviewModal>
      )}
    </div>
  );
}
